/**
 * features/quotes/QuotesManager.tsx
 * Phase 3 — Quotes list. Filter by status, search, open to edit/view.
 */
import { useMemo, useState } from 'react';
import { FileSignature, Plus, Search, Download, Mail } from 'lucide-react';
import { useQuotes } from './useQuotes';
import { QuoteEditor } from './QuoteEditor';
import { useSettings } from '@/contexts/SettingsContext';
import { downloadQuotePdf, quotePdfBase64, quoteFileName } from '@/lib/quotePdf';
import { formatPula } from '@/lib/money';
import { SendDocumentModal } from '@/features/common/SendDocumentModal';
import { DEFAULT_COMPANY_SETTINGS, type Quote, type QuoteStatus } from '@/types';
import { Button, Card, Input, Badge, Spinner } from '@/components/ui/primitives';

const TONE: Record<QuoteStatus, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  draft: 'neutral',
  sent: 'brand',
  accepted: 'success',
  declined: 'danger',
  expired: 'warning',
  converted: 'success',
};
const FILTERS: Array<{ key: 'all' | QuoteStatus; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'sent', label: 'Sent' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'converted', label: 'Converted' },
];
function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function QuotesManager() {
  const { quotes, loading } = useQuotes();
  const { settings } = useSettings();
  const company = settings ?? DEFAULT_COMPANY_SETTINGS;
  const [filter, setFilter] = useState<'all' | QuoteStatus>('all');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<Quote | null | 'new'>(null);
  const [sendQuoteDoc, setSendQuoteDoc] = useState<Quote | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quotes
      .filter((x) => (filter === 'all' ? true : x.status === filter))
      .filter((x) => !q || (x.number ?? '').toLowerCase().includes(q) || x.client.name.toLowerCase().includes(q));
  }, [quotes, filter, search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by number or client…" className="pl-9" />
        </div>
        <Button icon={<Plus size={16} />} onClick={() => setEditor('new')}>
          New quote
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1 text-sm font-semibold transition ${
              filter === f.key ? 'bg-brand text-white' : 'bg-black/5 text-primaryLight hover:bg-black/10 dark:bg-white/5 dark:text-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Spinner label="Loading quotes…" />
      ) : visible.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <FileSignature size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search || filter !== 'all' ? 'No quotes match.' : 'No quotes yet — create your first.'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Number</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Valid until</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((q) => (
                <tr
                  key={q.id}
                  onClick={() => setEditor(q)}
                  className="cursor-pointer border-b border-black/5 transition last:border-0 hover:bg-brandSoft/40 dark:border-white/5 dark:hover:bg-white/5"
                >
                  <td className="px-4 py-3 font-semibold text-primary dark:text-gray-100">
                    {q.number ?? <span className="text-primaryLight">Draft</span>}
                  </td>
                  <td className="px-4 py-3 text-primary dark:text-gray-200">{q.client.name}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(q.issueDate)}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(q.validUntil)}</td>
                  <td className="px-4 py-3 text-right text-primary dark:text-gray-200">{formatPula(q.totals.total)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={TONE[q.status]}>{q.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {q.number ? (
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => downloadQuotePdf(q, company)}
                          title="Download PDF"
                          className="rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => setSendQuoteDoc(q)}
                          title="Email quote"
                          className="rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                        >
                          <Mail size={16} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editor !== null && <QuoteEditor quote={editor === 'new' ? null : editor} onClose={() => setEditor(null)} />}

      {sendQuoteDoc && (
        <SendDocumentModal
          heading={`Email quote ${sendQuoteDoc.number}`}
          to={sendQuoteDoc.client.email ?? ''}
          subject={`Quotation ${sendQuoteDoc.number} from ${company.tradingName || company.legalName}`}
          message={`Dear ${sendQuoteDoc.client.name},\n\nPlease find attached quotation ${sendQuoteDoc.number} for ${formatPula(
            sendQuoteDoc.totals.total,
          )}.\n\nKind regards,\n${company.tradingName || company.legalName}`}
          filename={quoteFileName(sendQuoteDoc)}
          contentBase64={quotePdfBase64(sendQuoteDoc, company)}
          relatedEntityType="quote"
          relatedEntityId={sendQuoteDoc.id}
          onClose={() => setSendQuoteDoc(null)}
        />
      )}
    </div>
  );
}
